<template>
  <div class="form-designer">
    <!-- Header -->
    <div class="fd-header">
      <div class="fd-header-left">
        <feather-icon
          icon="LayoutIcon"
          size="18"
          class="mr-50"
        />
        <input
          v-model="formName"
          class="fd-name-input"
          placeholder="Название формы"
        >
      </div>
      <div class="fd-header-right">
        <span class="fd-badge">pivot_input</span>
        <b-button
          size="sm"
          variant="flat-secondary"
          @click="showJson = !showJson"
        >
          <feather-icon
            icon="CodeIcon"
            size="14"
          />
          {{ showJson ? 'Скрыть' : 'JSON' }}
        </b-button>
        <b-button
          size="sm"
          variant="outline-warning"
          :title="'Автоматически починить типовые проблемы формы'"
          @click="normalize"
        >
          <feather-icon
            icon="ZapIcon"
            size="14"
            class="mr-25"
          />
          Нормализовать
        </b-button>
        <b-button
          size="sm"
          variant="primary"
          :disabled="saving"
          @click="save"
        >
          <b-spinner
            v-if="saving"
            small
            class="mr-25"
          />
          <feather-icon
            v-else
            icon="SaveIcon"
            size="14"
            class="mr-25"
          />
          Сохранить
        </b-button>
      </div>
    </div>

    <div class="fd-body">
      <!-- Main area -->
      <div class="fd-main">
        <!-- Left: structure -->
        <div class="fd-structure">
          <!-- Params -->
          <div class="fd-section">
            <div class="fd-section-title">
              <feather-icon
                icon="SlidersIcon"
                size="14"
                class="mr-50"
              />
              Параметры формы
            </div>
            <div class="fd-params">
              <span
                v-for="(p, i) in params"
                :key="i"
                class="fd-param-chip"
              >
                {{ p.name }}
                <feather-icon
                  icon="XIcon"
                  size="12"
                  class="fd-param-remove"
                  @click="removeParam(i)"
                />
              </span>
              <span
                v-if="params.length === 0"
                class="text-muted"
                style="font-size:12px"
              >Нет параметров (required атрибуты формы)</span>
            </div>
          </div>

          <!-- Levels -->
          <div class="fd-section">
            <div class="fd-section-title">
              <feather-icon
                icon="LayersIcon"
                size="14"
                class="mr-50"
              />
              Уровни строк
              <span class="fd-section-count">{{ levels.length }}</span>
            </div>

            <draggable
              v-model="levels"
              handle=".fd-level-drag"
              ghost-class="fd-level-ghost"
            >
              <cbs-form-designer-level
                v-for="(level, idx) in levels"
                :key="'level-' + idx"
                :level="level"
                :index="idx"
                :total-levels="levels.length"
                :available-objects="availableObjects"
                :entity-map="entityMap"
                :palette-fields="paletteFields"
                :form-params="params"
                :parent-results="getParentResults(idx)"
                :child-level="levels[idx + 1] || null"
                :child-fields="getChildFields(idx)"
                @update="onLevelUpdate(idx, $event)"
                @remove="removeLevel(idx)"
                @add-column="onAddColumn(idx, $event)"
                @remove-column="onRemoveColumn(idx, $event)"
              />
            </draggable>

            <div
              class="fd-add-level"
              @click="addLevel"
            >
              <feather-icon
                icon="PlusIcon"
                size="14"
                class="mr-50"
              />
              Добавить уровень
            </div>
          </div>

          <!-- Grand Total -->
          <div class="fd-section">
            <div class="fd-section-title">
              <feather-icon
                icon="BarChart2Icon"
                size="14"
                class="mr-50"
              />
              Итоги
              <b-form-checkbox
                v-model="hasGrandTotal"
                switch
                size="sm"
                class="ml-1"
              />
            </div>
          </div>
        </div>

        <!-- Right: palette -->
        <cbs-form-designer-field-palette
          :fields="paletteFields"
          :loading="paletteLoading"
          @add-field="onPaletteAddField"
        />
      </div>

      <!-- JSON view -->
      <div
        v-if="showJson"
        class="fd-json"
      >
        <div class="fd-section-title">
          <feather-icon
            icon="CodeIcon"
            size="14"
            class="mr-50"
          />
          form_param JSON
        </div>
        <textarea
          :value="formParamJsonString"
          class="fd-json-editor"
          spellcheck="false"
          @input="onJsonEdit($event.target.value)"
        />
      </div>

      <!-- AI assistant -->
      <cbs-form-designer-ai
        :form-param="buildFormParam()"
        :object-id="objectFull.object.id"
        :entity-map="entityMap"
        @apply="onAiApply"
      />

      <!-- Preview -->
      <cbs-form-designer-preview
        :levels="levels"
        :column-heads="computedColumnHeads"
        :entity-map="entityMap"
        :has-grand-total="hasGrandTotal"
        :grand-total-columns="computedGrandTotalColumns"
      />
    </div>
  </div>
</template>

<script>
import { BButton, BFormCheckbox, BSpinner } from 'bootstrap-vue'
import draggable from 'vuedraggable'
import useCubus from '@/cubus/services/useCubus'
import useJwt from '@/cubus/jwt/useJwt'
import CbsFormDesignerLevel from './CbsFormDesignerLevel.vue'
import CbsFormDesignerFieldPalette from './CbsFormDesignerFieldPalette.vue'
import CbsFormDesignerPreview from './CbsFormDesignerPreview.vue'
import CbsFormDesignerAi from './CbsFormDesignerAi.vue'

export default {
  name: 'CbsFormDesigner',
  components: {
    BButton,
    BFormCheckbox,
    BSpinner,
    draggable,
    CbsFormDesignerLevel,
    CbsFormDesignerFieldPalette,
    CbsFormDesignerPreview,
    CbsFormDesignerAi,
  },
  props: {
    objectFull: { type: Object, required: true },
    allEntities: { type: Array, default: () => [] },
    allObjects: { type: Array, default: () => [] },
  },
  data() {
    return {
      formName: '',
      params: [],
      levels: [],
      hasGrandTotal: true,
      showJson: false,
      saving: false,
      paletteFields: [],
      paletteLoading: false,
      availableObjects: [],
      entityNames: {}, // entityId → { name, sid, datatype }
    }
  },
  computed: {
    entityMap() {
      // Merge allEntities + objectFull columns + palette fields
      const map = {}
      // From allEntities prop
      this.allEntities.forEach(e => {
        if (e.id) map[e.id] = { name: e.name || e.sid, sid: e.sid, datatype: e.datatype }
      })
      // From objectFull attributes
      if (this.objectFull && this.objectFull.attributes) {
        this.objectFull.attributes.forEach(a => {
          if (a.entityid && !map[a.entityid]) {
            map[a.entityid] = { name: a.label || a.entitysid, sid: a.entitysid, datatype: a.datatype }
          }
        })
      }
      // From palette fields (cube attributes)
      this.paletteFields.forEach(f => {
        if (f.entityId) map[f.entityId] = { name: f.name, sid: f.entitySid, datatype: f.datatype }
      })
      // From cached entityNames
      Object.entries(this.entityNames).forEach(([id, info]) => {
        if (!map[id]) map[id] = info
      })
      return map
    },
    computedGrandTotalColumns() {
      const fp = this.buildFormParam()
      return fp.grand_total?.column_heads || []
    },
    formParamJsonString() {
      return JSON.stringify(this.buildFormParam(), null, 2)
    },
    computedColumnHeads() {
      const recLevel = this.levels.find(l => l.type === 'record') || this.levels[this.levels.length - 1]
      if (!recLevel) return []
      return recLevel.columns.map(c => {
        if (c.type === 'spacer') return ''
        return this.entityName(c.entityId || c.destinationEntityId)
      })
    },
  },
  mounted() {
    this.init()
  },
  methods: {
    entityName(id) {
      if (!id) return ''
      const info = this.entityMap[id]
      return info ? info.name : `#${id}`
    },

    async init() {
      this.formName = this.objectFull.object.name
      this.loadAvailableObjects()
      this.loadFromExistingFormParam()
    },

    async loadAvailableObjects() {
      if (this.allObjects.length > 0) {
        this.availableObjects = this.allObjects.filter(o => o.name).sort((a, b) => a.name.localeCompare(b.name, 'ru'))
        return
      }
      // Load from API
      try {
        const result = await useCubus.loadQuery(this, { method: 'listobjects' })
        if (result && result.objects) {
          this.availableObjects = result.objects
            .filter(o => o.name && !o.del)
            .map(o => ({
              id: o.id, name: o.name, sid: o.sid, metaid: o.metaid,
            }))
            .sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'))
        }
      } catch (e) {
        console.error('Failed to load objects', e)
      }
    },

    loadFromExistingFormParam() {
      const prm = this.objectFull.params?.params?.find(p => p.paramname === 'form_param')
      if (!prm || !prm.jsonstring) {
        this.levels = [this.defaultLevel('group'), this.defaultLevel('record')]
        return
      }

      const fp = typeof prm.jsonstring === 'string' ? JSON.parse(prm.jsonstring) : prm.jsonstring
      this.formName = fp.name || this.objectFull.object.name
      this.levels = (fp.rows || []).map(row => this.rowToLevel(row))
      this.detectGroupModes()

      // Build entity names from column_heads
      if (fp.column_heads) {
        const recLevel = this.levels.find(l => l.type === 'record') || this.levels[this.levels.length - 1]
        if (recLevel) {
          fp.column_heads.forEach((head, i) => {
            if (head && recLevel.columns[i]) {
              const eid = recLevel.columns[i].entityId || recLevel.columns[i].destinationEntityId
              if (eid && head) {
                this.$set(this.entityNames, eid, { name: head, sid: '', datatype: '' })
              }
            }
          })
        }
      }

      // Parse params from required attributes
      this.params = (this.objectFull.attributes || [])
        .filter(a => a.isrequired)
        .map(a => ({ entityId: a.entityid, entitySid: a.entitysid, name: a.label || a.entitysid }))

      this.hasGrandTotal = !!fp.grand_total && Object.keys(fp.grand_total).length > 0
      this.loadPaletteFields()
      this.loadEntityNamesForAllLevels()
    },

    async loadEntityNamesForAllLevels() {
      // Collect all unique objectIds from levels and load their attributes
      const objectIds = [...new Set(this.levels.map(l => l.objectId).filter(Boolean))]
      for (const oid of objectIds) {
        if (this.paletteFields.some(f => f.entityId)) continue // already loaded
        try {
          const objFull = await useCubus.loadObject(this, { objectid: oid })
          if (objFull && objFull.attributes) {
            objFull.attributes.forEach(a => {
              if (a.entityid && !this.entityNames[a.entityid]) {
                this.$set(this.entityNames, a.entityid, {
                  name: a.label || a.entitysid,
                  sid: a.entitysid,
                  datatype: a.datatype,
                  refobjectid: a.refobjectid || 0,
                })
              }
            })
          }
        } catch (e) { /* skip */ }
      }
    },

    rowToLevel(row) {
      // If mode missing: 'group' only if row has groupby or results (means it aggregates children).
      // Otherwise a standalone row without children is treated as 'record'.
      const hasGroupby = row.groupby && row.groupby.groups && row.groupby.groups.length > 0
      const hasResults = Array.isArray(row.results) && row.results.length > 0
      const defaultType = (hasGroupby || hasResults) ? 'group' : 'record'
      return {
        type: row.mode || defaultType,
        objectId: row.objectid || null,
        addrecord: row.addrecord !== undefined ? row.addrecord : true,
        columns: (row.columns || []).map(c => ({
          entityId: c.entityid || null,
          type: c.type || (c.entityid === null && !c.type ? 'spacer' : 'data'),
          editable: c.editable || false,
          align: c.align || 'left',
          indent: c.indent || false,
          destinationEntityId: c.destination_entityid || null,
          scalarId: c.scalarid || null,
        })),
        results: (row.results || []).map(r => ({
          sourceEntityId: r.source_entityid,
          destinationEntityId: r.destination_entityid,
        })),
        filter: row.filter ? this.ensureFilterExpanded(row.filter) : null,
        groupby: row.groupby || null,
        orderby: row.orderby || null,
        expanded: true,
        groupMode: 'ref',
        groupEntity: null,
      }
    },

    // Detect group mode from form_param patterns:
    // TEXT: same objectId as child + groupby[0].entity = E + self-ref results on E → groupMode='text', groupEntity=E
    // REF:  results=[{source:1, destination:E}] where E is a ref-entity on child → groupMode='ref', groupEntity=E
    detectGroupModes() {
      for (let i = 0; i < this.levels.length - 1; i++) {
        const cur = this.levels[i]
        const next = this.levels[i + 1]
        if (cur.type !== 'group') continue

        // TEXT pattern
        if (cur.objectId && cur.objectId === next.objectId) {
          const groups = cur.groupby && cur.groupby.groups
          const entity = Array.isArray(groups) && groups[0] && groups[0].entity && groups[0].entity.value
          const hasSelfRef = entity && (cur.results || []).some(r => r.sourceEntityId === entity && r.destinationEntityId === entity)
          if (entity && hasSelfRef) {
            this.$set(this.levels, i, { ...cur, groupMode: 'text', groupEntity: entity })
            continue
          }
        }

        // REF pattern: results [{1, E}]
        const refResult = (cur.results || []).find(r => r.sourceEntityId === 1 && r.destinationEntityId)
        if (refResult) {
          this.$set(this.levels, i, { ...cur, groupMode: 'ref', groupEntity: refResult.destinationEntityId })
        }
      }
    },

    ensureFilterExpanded(filter) {
      if (!filter) return filter
      const f = JSON.parse(JSON.stringify(filter))
      if (f.node) {
        f.node.isexpanded = true
        if (!f.node.oper) f.node.oper = { id: 1, sid: 'and', name: 'And' }
        if (!f.node.conds) f.node.conds = []
        if (!f.node.nodes) f.node.nodes = []
        // Expand sub-nodes too
        if (f.node.nodes) {
          f.node.nodes.forEach(n => { n.isexpanded = true })
        }
      }
      return f
    },

    defaultLevel(type) {
      return {
        type,
        objectId: null,
        addrecord: type === 'record',
        columns: [],
        results: [],
        filter: null,
        groupby: null,
        orderby: null,
        expanded: true,
        groupMode: 'ref', // 'ref' — по справочнику (objectId); 'text' — по значению поля
        groupEntity: null, // entityId для groupMode='text'
      }
    },

    async loadPaletteFields() {
      const recLevel = this.levels.find(l => l.type === 'record')
      const objId = recLevel ? recLevel.objectId : (this.levels.length > 0 ? this.levels[this.levels.length - 1].objectId : null)
      if (!objId) return

      this.paletteLoading = true
      try {
        const objFull = await useCubus.loadObject(this, { objectid: objId })
        if (objFull && objFull.attributes) {
          this.paletteFields = objFull.attributes
            .filter(a => a.entityid && a.entitysid !== 'del' && a.entitysid !== 'sid')
            .map(a => ({
              entityId: a.entityid,
              entitySid: a.entitysid,
              name: a.label || a.entitysid,
              datatype: a.datatype,
              refobjectid: a.refobjectid || 0,
            }))
        }
      } catch (e) {
        console.error('Failed to load palette fields', e)
      }
      this.paletteLoading = false
    },

    // ─── Level operations ───────────────────────────
    addLevel() {
      const lastType = this.levels.length > 0 ? this.levels[this.levels.length - 1].type : 'group'
      this.levels.push(this.defaultLevel(lastType === 'record' ? 'group' : 'record'))
    },
    removeLevel(idx) {
      this.levels.splice(idx, 1)
    },
    onLevelUpdate(idx, updatedLevel) {
      this.$set(this.levels, idx, { ...this.levels[idx], ...updatedLevel })
      if (updatedLevel.objectId !== undefined) {
        this.loadPaletteFields()
      }
      // Auto-rebuild filters when results or type changes
      if (updatedLevel.results !== undefined || updatedLevel.type !== undefined) {
        this.autoGenerateFilters()
      }
    },

    // ─── Column operations (with auto-sync across levels) ──────────
    onAddColumn(levelIdx, field) {
      const isNumeric = ['double', 'int', 'money', 'decimal'].includes(field.datatype)
      const level = this.levels[levelIdx]

      // Add to target level
      level.columns.push({
        entityId: field.entityId,
        type: 'data',
        editable: true,
        align: isNumeric ? 'right' : 'left',
        destinationEntityId: null,
        scalarId: null,
        _new: true, // mark as new for highlighting
      })

      // Auto-sync: add corresponding column to ALL other levels
      this.levels.forEach((otherLevel, oi) => {
        if (oi === levelIdx) return
        if (otherLevel.type === 'record') {
          // Adding to a group → add data column to record
          otherLevel.columns.push({
            entityId: field.entityId,
            type: 'data',
            editable: true,
            align: isNumeric ? 'right' : 'left',
            destinationEntityId: null,
            scalarId: null,
            _new: true,
          })
        } else {
          // Adding to record → add aggr_sum (numeric) or spacer (text) to group
          if (isNumeric) {
            otherLevel.columns.push({
              entityId: null,
              type: 'aggr_sum',
              editable: false,
              align: 'right',
              destinationEntityId: field.entityId,
              scalarId: null,
              _new: true,
            })
          } else {
            otherLevel.columns.push({
              entityId: null,
              type: 'spacer',
              editable: false,
              align: 'left',
              destinationEntityId: null,
              scalarId: null,
              _new: true,
            })
          }
        }
      })

      // Clear _new markers after animation
      setTimeout(() => {
        this.levels.forEach(l => {
          l.columns.forEach(c => { delete c._new })
        })
        this.$forceUpdate()
      }, 2000)
    },

    onRemoveColumn(levelIdx, colIdx) {
      // Remove from target level
      this.levels[levelIdx].columns.splice(colIdx, 1)

      // Auto-sync: remove same position from all other levels
      this.levels.forEach((otherLevel, oi) => {
        if (oi === levelIdx) return
        if (colIdx < otherLevel.columns.length) {
          otherLevel.columns.splice(colIdx, 1)
        }
      })
    },

    onPaletteAddField(field) {
      // Add to record level — sync will propagate to groups
      const targetIdx = this.levels.findIndex(l => l.type === 'record')
      const idx = targetIdx >= 0 ? targetIdx : this.levels.length - 1
      if (idx >= 0) this.onAddColumn(idx, field)
    },

    getParentResults(levelIdx) {
      // Collect results from all levels above this one
      const results = []
      for (let i = 0; i < levelIdx; i++) {
        (this.levels[i].results || []).forEach(r => results.push(r))
      }
      return results
    },

    getChildFields(levelIdx) {
      // Return attribute fields of the child level's object (used by group entity picker)
      const child = this.levels[levelIdx + 1]
      if (!child || !child.objectId) return []
      // Prefer paletteFields if they belong to this child object
      const recLevel = this.levels.find(l => l.type === 'record')
      if (recLevel && recLevel.objectId === child.objectId && this.paletteFields.length > 0) {
        return this.paletteFields
      }
      // Fallback: extract entities used by child columns/results
      const fromChild = []
      const seen = new Set()
      const push = eid => {
        if (!eid || seen.has(eid)) return
        seen.add(eid)
        const info = this.entityNames[eid] || this.entityMap[eid] || {}
        fromChild.push({
          entityId: eid,
          name: info.name || `#${eid}`,
          entitySid: info.sid || '',
          datatype: info.datatype || '',
          refobjectid: info.refobjectid || 0,
        })
      }
      (child.columns || []).forEach(c => { push(c.entityId); push(c.destinationEntityId) });
      (child.results || []).forEach(r => { push(r.sourceEntityId); push(r.destinationEntityId) })
      return fromChild
    },

    // ─── Auto-generate filters ────────────────────────
    autoGenerateFilters() {
      // Collect all destination entities from group-level results
      const groupDestEntities = []
      this.levels.forEach((level, idx) => {
        if (level.type === 'record') return
        (level.results || []).forEach(r => {
          if (r.destinationEntityId) groupDestEntities.push({ entityId: r.destinationEntityId, levelIdx: idx })
        })
      })

      // For each level, build paramentity filters from PARENT levels
      this.levels.forEach((level, idx) => {
        if (idx === 0) return // first level has no parents
        const parentDestEntities = groupDestEntities.filter(g => g.levelIdx < idx)
        if (parentDestEntities.length === 0) return

        // Build filter conds: del=false + paramentity for each parent group entity
        const conds = [
          { isactive: true, oper: { sid: 'equal' }, args: [{ type: { sid: 'entity' }, value: 11 }, { type: { sid: 'value' }, value: false }] },
        ]
        parentDestEntities.forEach(p => {
          // Check if this condition already exists
          const existing = level.filter?.node?.conds?.find(c => c.args?.[0]?.value === p.entityId && c.args?.[1]?.type?.sid === 'paramentity')
          if (!existing) {
            conds.push({
              isactive: true,
              oper: { sid: 'equal' },
              args: [
                { type: { sid: 'entity' }, value: p.entityId },
                { type: { sid: 'paramentity' }, value: p.entityId },
              ],
            })
          } else {
            conds.push(existing)
          }
        })

        // Preserve other existing conditions (non-paramentity, non-del)
        // DROP paramentity conds that point to entities no longer passed by any ancestor
        if (level.filter?.node?.conds) {
          level.filter.node.conds.forEach(c => {
            const entVal = c.args?.[0]?.value
            if (entVal === 11) return // del=false already added
            const isParamentity = c.args?.[1]?.type?.sid === 'paramentity'
            if (isParamentity) {
              if (parentDestEntities.some(p => p.entityId === entVal)) return // already added above
              // Keep paramentity only if it references a required form param (scenario/year/BU)
              const paramVal = c.args?.[1]?.value
              const isFormParam = this.params.some(p => p.entityId === paramVal)
              if (!isFormParam) return // drop stale paramentity
            }
            conds.push(c)
          })
        }

        // Preserve existing nodes (OR conditions etc)
        const existingNodes = level.filter?.node?.nodes || []

        this.$set(this.levels[idx], 'filter', {
          isActive: true,
          node: {
            isactive: true, oper: { sid: 'and' }, nodes: existingNodes, conds,
          },
        })
      })
    },

    // ─── Params ─────────────────────────────────────
    removeParam(idx) {
      this.params.splice(idx, 1)
    },

    // ─── AI apply ────────────────────────────────────
    onAiApply(newFormParam) {
      // Apply AI-generated form_param to the designer
      if (!newFormParam) return
      this.formName = newFormParam.name || this.formName
      this.levels = (newFormParam.rows || []).map(r => this.rowToLevel(r))
      this.detectGroupModes()
      this.hasGrandTotal = !!newFormParam.grand_total && Object.keys(newFormParam.grand_total).length > 0
      this.loadPaletteFields()
      this.$bvToast.toast('Изменения AI применены. Нажмите "Сохранить" чтобы зафиксировать.', {
        title: 'AI', variant: 'info', solid: true, autoHideDelay: 4000,
      })
    },

    // ─── JSON ───────────────────────────────────────
    onJsonEdit(value) {
      try {
        const parsed = JSON.parse(value)
        this.formName = parsed.name || this.formName
        this.levels = (parsed.rows || []).map(r => this.rowToLevel(r))
        this.detectGroupModes()
        this.hasGrandTotal = !!parsed.grand_total && Object.keys(parsed.grand_total).length > 0
      } catch (e) { /* invalid json */ }
    },

    // ─── Build form_param ───────────────────────────
    buildFormParam() {
      const rows = this.levels.map((level, idx) => this.levelToRow(level, idx))
      const columnHeads = this.computedColumnHeads
      const grandTotal = this.hasGrandTotal ? {
        column_heads: columnHeads.map((_, i) => {
          const recLevel = this.levels.find(l => l.type === 'record') || this.levels[this.levels.length - 1]
          if (!recLevel) return null
          const col = recLevel.columns[i]
          if (!col || col.type === 'spacer') return null
          const eid = col.entityId || col.destinationEntityId
          const info = eid ? this.entityMap[eid] : null
          if (info && ['double', 'int', 'money', 'decimal'].includes(info.datatype)) {
            return { type: 'sumrows', align: 'right', entityid: eid }
          }
          return null
        }),
      } : {}

      return {
        name: this.formName, column_heads: columnHeads, rows, grand_total: grandTotal,
      }
    },

    levelToRow(level, idx) {
      const isTextGroup = level.type === 'group' && level.groupMode === 'text' && level.groupEntity
      // For text-group: inherit objectId from child level
      let { objectId } = level
      if (isTextGroup && idx !== undefined) {
        const child = this.levels[idx + 1]
        if (child && child.objectId) objectId = child.objectId
      }

      const row = {
        objectid: objectId,
        columns: level.columns.map(c => {
          if (c.type === 'spacer') return { entityid: null }
          if (c.type === 'aggr_sum') {
            return {
              type: 'aggr_sum', align: 'right', destination_entityid: c.destinationEntityId, entityid: null,
            }
          }
          if (c.type === 'scalar') {
            return {
              type: 'scalar', align: 'right', scalarid: c.scalarId, destination_entityid: c.destinationEntityId,
            }
          }
          const col = { entityid: c.entityId }
          if (c.editable) col.editable = true
          if (c.align === 'right') col.align = 'right'
          if (c.indent) col.indent = true
          return col
        }),
        results: isTextGroup
          ? [{ source_entityid: level.groupEntity, destination_entityid: level.groupEntity }]
          : level.results.map(r => ({ source_entityid: r.sourceEntityId, destination_entityid: r.destinationEntityId })),
      }
      if (level.type !== 'group') row.mode = level.type
      row.addrecord = !!level.addrecord
      if (level.filter) row.filter = level.filter
      if (isTextGroup) {
        row.groupby = {
          isactive: true,
          groups: [{
            isactive: true,
            oper: { sid: 'group', name: 'Group' },
            entity: { value: level.groupEntity },
          }],
        }
      } else if (level.groupby) {
        row.groupby = level.groupby
      }
      if (level.orderby) row.orderby = level.orderby
      return row
    },

    // ─── Validation ─────────────────────────────────
    validateForm() {
      const issues = []
      if (!this.levels || this.levels.length === 0) {
        issues.push('Нет ни одного уровня')
        return issues
      }
      // Last level must be record/tree_record
      const last = this.levels[this.levels.length - 1]
      if (last.type !== 'record' && last.type !== 'tree_record') {
        issues.push(`Последний уровень должен быть "Запись", сейчас: "${last.type}"`)
      }
      // Each level must have objectId
      this.levels.forEach((lv, i) => {
        if (!lv.objectId) issues.push(`Уровень ${i + 1}: не задан источник данных`)
      })
      // Group levels: must have results (for drill-down)
      for (let i = 0; i < this.levels.length - 1; i += 1) {
        const lv = this.levels[i]
        if (lv.type === 'group') {
          if (!Array.isArray(lv.results) || lv.results.length === 0) {
            issues.push(`Уровень ${i + 1} (группа): нет связки с дочерним уровнем (results)`)
          } else {
            lv.results.forEach((r, ri) => {
              if (!r.sourceEntityId) issues.push(`Уровень ${i + 1}, связка ${ri + 1}: не задано поле-источник`)
              if (!r.destinationEntityId) issues.push(`Уровень ${i + 1}, связка ${ri + 1}: не задано поле-назначение`)
            })
          }
          // Text-group consistency
          if (lv.groupMode === 'text') {
            const next = this.levels[i + 1]
            if (next && lv.objectId !== next.objectId) {
              issues.push(`Уровень ${i + 1} (text-группа): objectId должен совпадать с дочерним (${next.objectId})`)
            }
            if (!lv.groupEntity) issues.push(`Уровень ${i + 1} (text-группа): не выбрано поле для группировки`)
          }
        }
        // Columns count alignment with record level (warn, not error)
        if (this.levels.length > 1) {
          const recColsLen = last.columns.length
          if (lv.columns.length !== recColsLen && lv.columns.length > 0) {
            issues.push(`Уровень ${i + 1}: колонок ${lv.columns.length}, на record-уровне ${recColsLen} — могут не совпасть визуально`)
          }
        }
        // aggr_sum must have destination_entityid
        lv.columns.forEach((c, ci) => {
          if (c.type === 'aggr_sum' && !c.destinationEntityId) {
            issues.push(`Уровень ${i + 1}, колонка ${ci + 1} (aggr_sum): не указан destination entity`)
          }
        })
      }
      // Record level must have at least one column
      if (last.columns.length === 0) {
        issues.push('Record-уровень: нет колонок')
      }
      // Orphan paramentity — not passed by any ancestor and not a form param
      const validParamEntities = new Set()
      this.params.forEach(p => validParamEntities.add(p.entityId))
      this.levels.forEach((lv, i) => {
        (lv.results || []).forEach(r => {
          if (r.destinationEntityId) {
            // available for all descendants
            for (let j = i + 1; j < this.levels.length; j += 1) validParamEntities.add(r.destinationEntityId)
          }
        })
      })
      this.levels.forEach((lv, i) => {
        const conds = lv.filter?.node?.conds || []
        conds.forEach(c => {
          if (c.args?.[1]?.type?.sid !== 'paramentity') return
          const pval = c.args[1].value
          if (!validParamEntities.has(pval)) {
            issues.push(`Уровень ${i + 1}: paramentity #${pval} не передан ни одним родителем — запросом не резолвится (ошибка no_param_${pval})`)
          }
        })
      })
      return issues
    },

    // ─── Normalize — auto-fix common problems ──────
    normalize() {
      const fixed = []
      // 1. Ensure last level is record
      if (this.levels.length > 0) {
        const last = this.levels[this.levels.length - 1]
        if (last.type === 'group') {
          this.$set(this.levels, this.levels.length - 1, { ...last, type: 'record', addrecord: true })
          fixed.push('Последний уровень: group → record')
        }
      }
      // 2. For each group level, rebuild results if missing based on groupMode
      this.levels.forEach((lv, i) => {
        if (i === this.levels.length - 1) return
        if (lv.type !== 'group') return
        const child = this.levels[i + 1]
        // text-group: ensure objectId = child & self-ref results + groupby
        if (lv.groupMode === 'text' && lv.groupEntity) {
          let changed = false
          const nextLv = { ...lv }
          if (nextLv.objectId !== child.objectId) { nextLv.objectId = child.objectId; changed = true }
          if (!nextLv.results || nextLv.results.length !== 1
              || nextLv.results[0].sourceEntityId !== lv.groupEntity
              || nextLv.results[0].destinationEntityId !== lv.groupEntity) {
            nextLv.results = [{ sourceEntityId: lv.groupEntity, destinationEntityId: lv.groupEntity }]
            changed = true
          }
          if (!nextLv.groupby || !nextLv.groupby.groups || !nextLv.groupby.groups[0]
              || nextLv.groupby.groups[0].entity?.value !== lv.groupEntity) {
            nextLv.groupby = {
              isactive: true,
              groups: [{ isactive: true, oper: { sid: 'group', name: 'Group' }, entity: { value: lv.groupEntity } }],
            }
            changed = true
          }
          if (changed) {
            this.$set(this.levels, i, nextLv)
            fixed.push(`Уровень ${i + 1}: восстановлена структура text-группы`)
          }
        }
        // ref-group: ensure results = [{1, groupEntity}] if groupEntity set
        if (lv.groupMode === 'ref' && lv.groupEntity) {
          const r = (lv.results || [])[0]
          if (!r || r.sourceEntityId !== 1 || r.destinationEntityId !== lv.groupEntity) {
            this.$set(this.levels, i, { ...lv, results: [{ sourceEntityId: 1, destinationEntityId: lv.groupEntity }] })
            fixed.push(`Уровень ${i + 1}: восстановлена связка ref-группы`)
          }
        }
      })
      // 3. Remove empty columns trailing beyond record level length
      if (this.levels.length > 1) {
        const recLen = this.levels[this.levels.length - 1].columns.length
        this.levels.forEach((lv, i) => {
          if (i === this.levels.length - 1) return
          if (lv.columns.length > recLen) {
            this.$set(this.levels, i, { ...lv, columns: lv.columns.slice(0, recLen) })
            fixed.push(`Уровень ${i + 1}: обрезаны лишние колонки (было ${lv.columns.length})`)
          }
          while (lv.columns.length < recLen) {
            lv.columns.push({ type: 'spacer', entityId: null })
            fixed.push(`Уровень ${i + 1}: добавлен spacer`)
          }
        })
      }
      // 4. Autogenerate paramentity filters from ancestor results
      this.autoGenerateFilters()
      fixed.push('Фильтры paramentity пересобраны')

      const msg = fixed.length > 0
        ? `Исправлено:<br>• ${fixed.join('<br>• ')}`
        : 'Проблем не найдено'
      this.$bvToast.toast(msg, {
        title: 'Нормализация',
        variant: fixed.length ? 'success' : 'info',
        solid: true,
        autoHideDelay: 6000,
      })
    },

    // ─── Save ───────────────────────────────────────
    async save() {
      const issues = this.validateForm()
      if (issues.length > 0) {
        const issuesHtml = issues.map(s => `• ${s}`).join('<br>')
        const Swal = require('sweetalert2/dist/sweetalert2.js').default
        const res = await Swal.fire({
          title: 'В форме есть проблемы',
          html: `<div style="text-align:left;font-size:13px;max-height:300px;overflow-y:auto">${issuesHtml}</div>`,
          icon: 'warning',
          showCancelButton: true,
          showDenyButton: true,
          confirmButtonText: 'Всё равно сохранить',
          denyButtonText: '⚡ Нормализовать',
          cancelButtonText: 'Отмена',
        })
        if (res.isDismissed) return
        if (res.isDenied) {
          this.normalize()
          return
        }
      }
      this.saving = true
      try {
        const fp = this.buildFormParam()
        const resp = await useJwt.query({
          token: useJwt.getCubusToken(),
          query: {
            method: 'updateformparam',
            mode: 'sync',
            param: {
              objectid: this.objectFull.object.id,
              formparam: fp,
            },
          },
        })
        const { data } = resp
        if (data.result?.success || data.thread) {
          this.$bvToast.toast('form_param сохранён', {
            title: 'Успех', variant: 'success', solid: true, autoHideDelay: 3000,
          })
        } else if (data.error) {
          this.$bvToast.toast(data.error, { title: 'Ошибка', variant: 'danger', solid: true })
        }
      } catch (e) {
        console.error('[FormDesigner] Save error', e)
        this.$bvToast.toast(e.message || 'Ошибка сохранения', { title: 'Ошибка', variant: 'danger', solid: true })
      }
      this.saving = false
    },
  },
}
</script>

<style scoped>
.form-designer { background: #fff; border: 1px solid #e0e0e0; border-radius: 8px; overflow: hidden; }
.fd-header { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; border-bottom: 1px solid #eee; background: #f8f9fa; }
.fd-header-left { display: flex; align-items: center; }
.fd-name-input { border: none; background: transparent; font-size: 16px; font-weight: 600; outline: none; min-width: 300px; }
.fd-name-input:focus { border-bottom: 2px solid #7367f0; }
.fd-header-right { display: flex; align-items: center; gap: 8px; }
.fd-badge { font-size: 11px; padding: 2px 8px; background: #e8e5ff; color: #7367f0; border-radius: 4px; }
.fd-body { padding: 16px; }
.fd-main { display: flex; gap: 16px; }
.fd-structure { flex: 1; min-width: 0; }
.fd-section { margin-bottom: 16px; }
.fd-section-title { display: flex; align-items: center; font-size: 13px; font-weight: 600; color: #555; margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.5px; }
.fd-section-count { margin-left: 6px; font-size: 11px; color: #999; font-weight: 400; }
.fd-params { display: flex; flex-wrap: wrap; gap: 6px; }
.fd-param-chip { display: inline-flex; align-items: center; gap: 4px; padding: 4px 10px; background: #e8f5e9; color: #2e7d32; border-radius: 16px; font-size: 12px; }
.fd-param-remove { cursor: pointer; opacity: 0.6; }
.fd-param-remove:hover { opacity: 1; }
.fd-add-level { display: flex; align-items: center; justify-content: center; padding: 10px; border: 2px dashed #ddd; border-radius: 6px; color: #999; cursor: pointer; font-size: 13px; transition: all 0.2s; }
.fd-add-level:hover { border-color: #7367f0; color: #7367f0; background: #f8f7ff; }
.fd-level-ghost { opacity: 0.4; background: #e8e5ff; }
.fd-json { margin-top: 16px; border-top: 1px solid #eee; padding-top: 12px; }
.fd-json-editor { width: 100%; min-height: 200px; max-height: 400px; font-family: 'Fira Code', 'Consolas', monospace; font-size: 12px; padding: 8px; border: 1px solid #ddd; border-radius: 4px; resize: vertical; background: #fafafa; line-height: 1.4; }
</style>
